import { Bookable } from '@app/domain/Bookable'
import { Booking } from '@app/domain/Booking'
import { BookingReason } from '@app/domain/BookingReason'
import { DateComponent } from '@app/domain/DateComponent'
import { FloorPlan } from '@app/domain/FloorPlan'
import { PartySizeDuration } from '@app/domain/PartySizeDuration'
import { Table } from '@app/domain/Table'
import { TableCombination } from '@app/domain/TableCombination'

export class Area {

    constructor(
        public id: string,
        public displayName: string,
        public description: string | null,
        tableTurnaroundInterval: number,
        public automaticAcceptSchedule: DateComponent[],
        public automaticAcceptPartySize: number | null,
        public displayOrder: number,
        public isActive: boolean,
        public shouldDeclareIfNotChosen: boolean,
        public tables: Table[],
        public combinations: TableCombination[],
        public scheduleIds: string[],
        public floorPlans: FloorPlan[],
        public dateCreated: Date,
        public dateUpdated: Date,
        public tableTurnaroundIntervals: PartySizeDuration[] = [],
    ) {
        if (this.tableTurnaroundIntervals.length === 0) {
            this.tableTurnaroundIntervals = [
                new PartySizeDuration(1, tableTurnaroundInterval),
            ]
        }
        this.tableTurnaroundIntervals = [...this.tableTurnaroundIntervals]
            .sort((a, b) => (a.partySize ?? 1) - (b.partySize ?? 1))
    }

    get tableTurnaroundInterval(): number {
        return this.tableTurnaroundIntervals[0]?.duration ?? 0
    }

    tableTurnaroundIntervalForPartySize(partySize: number): number {
        const interval = this.tableTurnaroundIntervals
            .filter(interval => (interval.partySize ?? 1) <= partySize)
            .at(-1)
        return interval?.duration ?? this.tableTurnaroundInterval
    }

    getTableWithId(id: string): Table | null {
        return this.tables.find(t => t.id === id) || null
    }

    getDisplayedTablesBookingIsOn(booking: Booking): Table[] {
        return this.tablesSortedByDisplayOrder
            .filter(table => booking.tableIds.includes(table.id))
    }

    getContiguousTablesForBooking(booking: Booking): Table[] {
        const tables = this.tablesSortedByDisplayOrder
        const tablesUsedForBooking = this.tablesUsedForBooking(booking)
        sortTablesByDisplayOrderInArea(tablesUsedForBooking, this)
        const tableIndexes = tablesUsedForBooking
            .map(table => table.id)
            .map(tableId => tables.findIndex(table => table.id === tableId))
        const firstIndex = tableIndexes[0]
        const contiguousTableIndexes = tableIndexes.filter((index, i) => index === firstIndex + i)
        return contiguousTableIndexes.map(index => tables[index])
    }

    getNonContiguousTablesForBooking(booking: Booking): Table[] {
        const contiguousTables = this.getContiguousTablesForBooking(booking)
        return this.tablesUsedForBooking(booking)
            .filter(table => !contiguousTables.includes(table))
    }

    addTable(table: Table) {
        this.tables = [
            ...this.tables,
            table,
        ]
    }

    updateTable(table: Table) {
        const index = this.tables.findIndex(t => t.id === table.id)
        this.tables = [
            ...this.tables.slice(0, index),
            table,
            ...this.tables.slice(index + 1),
        ]
    }

    removeTable(table: Table) {
        const index = this.tables.findIndex(t => t.id === table.id)
        this.tables = [
            ...this.tables.slice(0, index),
            ...this.tables.slice(index + 1),
        ]
    }

    addCombination(combination: TableCombination) {
        this.combinations = [
            ...this.combinations,
            combination,
        ]
    }

    updateCombination(combination: TableCombination) {
        const index = this.combinations.findIndex(c => c.id === combination.id)
        this.combinations = [
            ...this.combinations.slice(0, index),
            combination,
            ...this.combinations.slice(index + 1),
        ]
    }

    removeCombination(combination: TableCombination) {
        const index = this.combinations.findIndex(c => c.id === combination.id)
        this.combinations = [
            ...this.combinations.slice(0, index),
            ...this.combinations.slice(index + 1),
        ]
    }

    get availableTables(): Table[] {
        return this.tables.sort((table => table.bookingOrder))
    }

    minimumPartySize(): number {
        return Math.min(
            ...this.tables
                .map(table => table.minimumSeats)
        )
    }

    maximumPartySize(): number {
        const sumTablesMaxSeats = this.tables
            .reduce((total, table) => total + table.maximumSeats, 0)
        const sumCombinationsMaxSeats = this.combinations
            .reduce((total, combination) => total + combination.maximumSeats, 0)
        const maxCapacity = Math.max(sumTablesMaxSeats, sumCombinationsMaxSeats)
        const limit = 100
        return Math.min(maxCapacity, limit)
    }

    tablesUsedForBooking(booking: Booking): Table[] {
        return booking.tableIds.flatMap(tableId => {
            return this.tables.filter(table => table.id === tableId)
        })
    }

    tablesReservableForReasonOrNone(reason: BookingReason | null): Table[] {
        if (reason === null) {
            return this.tables.filter(table => table.reasonIds.length === 0)
        }
        return this.tables.filter(table => table.isReservableForReasonWithId(reason.id))
    }

    tablesReservableForReasonOrNoneOnDate(
        reason: BookingReason | null,
        date: Date,
        timeZone: string,
    ): Table[] {
        const tables = this.tablesReservableForReasonOrNone(reason)
        if (reason === null) {
            return tables
        }
        if (reason.appliesToDate(date, timeZone)) {
            return tables
        }
        return []
    }

    doTablesFitPartySize(tables: Table[], partySize: number): boolean {
        const options = this.partySizeOptionsForTables(tables)
        if (options.length === 0) {
            return false
        }
        return options.includes(partySize)
    }

    partySizeOptionsForTables(tables: Table[]): number[] {
        const combination = this.combinations.find(combination => {
            return combination.isFormedFromTables(tables)
        }) || null
        const minimumSeats = combination ?
            combination.minimumSeats :
            tables.reduce((total, table) => total + table.minimumSeats, 0)
        const maximumSeats = combination ?
            combination.maximumSeats :
            tables.reduce((total, table) => total + table.maximumSeats, 0)
        if (minimumSeats > maximumSeats) {
            return []
        }
        const options: number[] = []
        for (let size = minimumSeats; size <= maximumSeats; size++) {
            options.push(size)
        }
        return options
    }

    getTablesInBookable(bookable: Bookable): Table[] {
        return this.tables.filter(table => bookable.tableIds.includes(table.id))
    }

    get tablesSortedByDisplayOrder() {
        const tables = [...this.tables]
        sortTablesByDisplayOrderInArea(tables, this)
        return tables
    }

    get tablesSortedByBookingOrder() {
        return [...this.tables].sort((a, b) => a.bookingOrder - b.bookingOrder)
    }

    get combinationsSortedByBookingOrder() {
        return [...this.combinations].sort((a, b) => a.bookingOrder - b.bookingOrder)
    }

    get scheduledForManualAcceptance(): boolean {
        return this.automaticAcceptSchedule.length < 7 || this.automaticAcceptPartySize !== null
    }

    totalPartySize(): number {
        return this.tables.reduce((total, table) => total + table.maximumSeats, 0)
    }

    contiguouslyDisplayedTablesInListOnDate(tableIds: string[], date: Date): Table[][] {
        const allTables = [...this.tablesOnDate(date)]
        sortTablesByDisplayOrderInArea(allTables, this)
        const tables = allTables.filter(table => tableIds.includes(table.id))
        const contiguousTables: Table[][] = []
        let currentContiguousTables: Table[] = []
        let previousTable: Table | null = null
        for (const table of tables) {
            if (previousTable === null) {
                currentContiguousTables.push(table)
                previousTable = table
                continue
            }
            const previousTableIndex = allTables.indexOf(previousTable)
            const tableIndex = allTables.indexOf(table)
            if (previousTableIndex === -1) {
                return []
            }
            if (tableIndex === -1) {
                return []
            }
            if (tableIndex === previousTableIndex + 1) {
                currentContiguousTables.push(table)
                previousTable = table
                continue
            }
            contiguousTables.push(currentContiguousTables)
            currentContiguousTables = [table]
            previousTable = table
        }
        if (currentContiguousTables.length > 0) {
            contiguousTables.push(currentContiguousTables)
        }
        return contiguousTables
    }

    get defaultFloorPlan(): FloorPlan | null {
        return this.floorPlans.find(fp => fp.isDefault) ?? null
    }

    activeFloorPlanForDate(date: Date): FloorPlan | null {
        const override = this.floorPlans.find(fp => fp.containsDate(date))
        if (override) {
            return override
        }
        return this.defaultFloorPlan
    }

    tablesOnDate(date: Date): Table[] {
        const activeFloorPlan = this.activeFloorPlanForDate(date)
        if (activeFloorPlan) {
            return this.tablesForFloorPlan(activeFloorPlan)
        }
        return this.tables
    }

    combinationsOnDate(date: Date): TableCombination[] {
        const activeFloorPlan = this.activeFloorPlanForDate(date)
        if (activeFloorPlan) {
            return this.combinationsForFloorPlan(activeFloorPlan)
        }
        return this.combinations
    }

    tablesForFloorPlan(floorPlan: FloorPlan): Table[] {
        const tableIdSet = new Set(floorPlan.tableIds)
        return this.tables.filter(t => tableIdSet.has(t.id))
    }

    combinationsForFloorPlan(floorPlan: FloorPlan): TableCombination[] {
        const tableIdSet = new Set(floorPlan.tableIds)
        return this.combinations.filter(c =>
            c.tableIds.every(id => tableIdSet.has(id))
        )
    }

    getFloorPlanWithId(id: string): FloorPlan | null {
        return this.floorPlans.find(fp => fp.id === id) ?? null
    }

    addFloorPlan(floorPlan: FloorPlan) {
        this.floorPlans = [...this.floorPlans, floorPlan]
    }

    updateFloorPlan(floorPlan: FloorPlan) {
        const index = this.floorPlans.findIndex(fp => fp.id === floorPlan.id)
        if (index !== -1) {
            this.floorPlans = [
                ...this.floorPlans.slice(0, index),
                floorPlan,
                ...this.floorPlans.slice(index + 1),
            ]
        }
    }

    removeFloorPlan(floorPlan: FloorPlan) {
        const index = this.floorPlans.findIndex(fp => fp.id === floorPlan.id)
        if (index !== -1) {
            this.floorPlans = [
                ...this.floorPlans.slice(0, index),
                ...this.floorPlans.slice(index + 1),
            ]
        }
    }

    removeTableFromAllFloorPlans(tableId: string) {
        this.floorPlans = this.floorPlans.map(fp => {
            if (fp.tableIds.includes(tableId)) {
                return new FloorPlan(
                    fp.id,
                    fp.displayName,
                    fp.dateRanges,
                    fp.tableIds.filter(id => id !== tableId),
                    fp.dateCreated,
                    fp.dateUpdated
                )
            }
            return fp
        })
    }
}

export function sortTablesByDisplayOrderInArea(tables: Table[], area: Area) {
    tables.sort((a, b) => {
        const aIndex = area.tables.indexOf(a) ?? 0
        const bIndex = area.tables.indexOf(b) ?? 0
        const displayOrderDifference = a.displayOrder - b.displayOrder
        const indexDifference = aIndex - bIndex
        if (displayOrderDifference === 0) {
            return indexDifference
        }
        return displayOrderDifference
    })
}

export function sortTablesByBookingOrderInArea(tables: Table[], area: Area) {
    tables.sort((a, b) => {
        const aIndex = area.tables.indexOf(a) ?? 0
        const bIndex = area.tables.indexOf(b) ?? 0
        const bookingOrderDifference = a.bookingOrder - b.bookingOrder
        const indexDifference = aIndex - bIndex
        if (bookingOrderDifference === 0) {
            return indexDifference
        }
        return bookingOrderDifference
    })
}

export function sortCombinationsByBookingOrderInArea(combinations: TableCombination[], area: Area) {
    combinations.sort((a, b) => {
        const aIndex = area.combinations.indexOf(a) ?? 0
        const bIndex = area.combinations.indexOf(b) ?? 0
        const bookingOrderDifference = a.bookingOrder - b.bookingOrder
        const indexDifference = aIndex - bIndex
        if (bookingOrderDifference === 0) {
            return indexDifference
        }
        return bookingOrderDifference
    })
}
